import { useCallback, useEffect, useState } from 'react';
import { useUserTokenDecoded } from '../../../../redux/slices/appInfo/hooks';

const MAX_RECENT = 6;

/** The local storage key of one user's recent searches (no API: design b1 note). */
export function recentSearchesKey(userId: number | string): string {
  return `m24BrowseRecent:${userId}`;
}

function read(key: string | null): string[] {
  if (key === null) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw === null ? [] : JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function write(key: string | null, items: string[]): void {
  if (key === null) {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Private mode or a full store: the list stays in memory for this visit.
  }
}

/** Adds a query at the top, without a second copy, and keeps the newest six. Pure. */
export function withRecentSearch(items: string[], query: string): string[] {
  const trimmed = query.trim();
  if (trimmed === '') {
    return items;
  }
  const rest = items.filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
  return [trimmed, ...rest].slice(0, MAX_RECENT);
}

/** The recent searches of the signed-in user, kept in this browser only. */
export function useRecentSearches() {
  const user = useUserTokenDecoded();
  const key = user?.id !== undefined ? recentSearchesKey(user.id) : null;
  const [items, setItems] = useState<string[]>(() => read(key));

  useEffect(() => {
    setItems(read(key));
  }, [key]);

  const add = useCallback(
    (query: string) => {
      setItems((current) => {
        const next = withRecentSearch(current, query);
        write(key, next);
        return next;
      });
    },
    [key]
  );

  const clear = useCallback(() => {
    write(key, []);
    setItems([]);
  }, [key]);

  return { items, add, clear };
}
